/**
 * Widget d'affichage des paiements groupés par méthode (Cash vs En Ligne)
 * Affiche les montants par devise avec pagination infinie
 * Détails : Location, Commission, Taxes par devise
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Button,
  Collapse,
  Avatar,
  Paper,
  IconButton,
  Skeleton,
  LinearProgress,
  Tooltip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Payment as PaymentIcon,
  MonetizationOn as CashIcon,
  CreditCard as OnlineIcon,
  AccountBalance as RentalIcon,
  BusinessCenter as CommissionIcon,
  Receipt as TaxIcon,
  MoreVert as MoreIcon,
  Refresh as RefreshIcon,
  CurrencyExchange as CurrencyIcon,
  DateRange as DateRangeIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { formatPrice } from '../../config/currencies';
import { formatDate, formatDateTime } from '../../utils/formatters';
import * as apiClient from '../../utils/apiClient';

const OwnerPaymentMethodsWidget = () => {
  const { t } = useTranslation('financial');
  const { user } = useAuth();
  
  // États principaux
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  // États pour la pagination infinie
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const limit = 5; // Devises par page
  
  // États pour les filtres
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Gestion de l'expansion
  const handleToggle = () => {
    setExpanded(!expanded);
  };

  // Gestion des filtres de date
  const handleDateFromChange = (event) => {
    setDateFrom(event.target.value);
  };

  const handleDateToChange = (event) => {
    setDateTo(event.target.value);
  };

  const handleClearFilters = () => {
    setDateFrom(null);
    setDateTo(null);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Chargement initial des données
  const loadPaymentMethods = useCallback(async (isLoadingMore = false) => {
    if (!user) return;
    
    try {
      if (isLoadingMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setOffset(0);
      }
      
      console.log('Loading owner payments by method', { 
        isLoadingMore, 
        offset: isLoadingMore ? offset : 0 
      });
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (isLoadingMore ? offset : 0).toString()
      });
      
      // Ajouter les filtres de date si définis
      if (dateFrom) {
        params.append('dateFrom', dateFrom);
      }
      if (dateTo) {
        params.append('dateTo', dateTo);
      }
      
      const response = await apiClient.get(`/financial/owner/payments-by-method?${params}`);
      
      if (response.success && response.data) {
        const newData = response.data;
        
        if (isLoadingMore) {
          // Fusionner les nouvelles données avec les existantes
          setData(prevData => ({
            ...newData,
            cash_payments: [...(prevData?.cash_payments || []), ...newData.cash_payments],
            online_payments: [...(prevData?.online_payments || []), ...newData.online_payments]
          }));
          setOffset(prev => prev + limit);
        } else {
          // Remplacer toutes les données
          setData(newData);
          setOffset(limit);
        }
        
        setHasMore(newData.pagination.has_more);
        
        console.log('Owner payments by method loaded:', {
          cash_count: newData.cash_payments.length,
          online_count: newData.online_payments.length,
          has_more: newData.pagination.has_more,
          total: newData.pagination.total
        });
        
      } else {
        throw new Error('Données de paiements invalides');
      }
      
    } catch (err) {
      console.error('Error loading payments by method:', err);
      setError(err.message || 'Erreur lors du chargement des paiements');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [user, dateFrom, dateTo]);

  // Chargement de plus de devises
  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadPaymentMethods(true);
    }
  }, [loadingMore, hasMore, loadPaymentMethods]);

  // Rafraîchissement des données
  const handleRefresh = useCallback(() => {
    setError(null);
    setOffset(0);
    setHasMore(true);
    loadPaymentMethods(false);
  }, [loadPaymentMethods]);

  // Effet de chargement initial
  useEffect(() => {
    if (user && expanded) {
      loadPaymentMethods(false);
    }
  }, [user, expanded, dateFrom, dateTo]);

  // Rendu d'une carte de devise
  const renderCurrencyCard = (currencyData, paymentMethod) => {
    if (!currencyData) return null;
    
    const icon = paymentMethod === 'cash' ? <CashIcon /> : <OnlineIcon />;
    const bgColor = paymentMethod === 'cash' ? 'rgba(76, 175, 80, 0.08)' : 'rgba(33, 150, 243, 0.08)';
    const borderColor = paymentMethod === 'cash' ? '#4caf50' : '#2196f3';
    
    return (
      <Paper
        key={`${paymentMethod}-${currencyData.currency}`}
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          backgroundColor: bgColor,
          border: `1px solid ${borderColor}20`,
          borderRadius: 2,
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            backgroundColor: paymentMethod === 'cash' ? 'rgba(76, 175, 80, 0.12)' : 'rgba(33, 150, 243, 0.12)'
          }
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar sx={{ 
              bgcolor: borderColor, 
              width: 32, 
              height: 32 
            }}>
              {icon}
            </Avatar>
            <Typography variant="h6" fontWeight="bold">
              {formatPrice(currencyData.total_amount, currencyData.currency)}
            </Typography>
          </Box>
          <Chip 
            label={t('paymentMethodsWidget.currencyTransactions', { count: currencyData.transaction_count })}
            size="small"
            sx={{ 
              bgcolor: borderColor,
              color: 'white',
              fontWeight: 'medium'
            }}
          />
        </Box>
        
        {/* Détails des montants */}
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <RentalIcon sx={{ fontSize: 16, color: '#1976d2' }} />
              <Typography variant="body2" color="text.secondary">
                {t('paymentMethodsWidget.rental')}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {formatPrice(currencyData.rental_amount, currencyData.currency)}
            </Typography>
          </Grid>
          
          <Grid item xs={4}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <CommissionIcon sx={{ fontSize: 16, color: '#ff9800' }} />
              <Typography variant="body2" color="text.secondary">
                {t('paymentMethodsWidget.commission')}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {formatPrice(currencyData.commission_amount, currencyData.currency)}
            </Typography>
          </Grid>
          
          <Grid item xs={4}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <TaxIcon sx={{ fontSize: 16, color: '#f44336' }} />
              <Typography variant="body2" color="text.secondary">
                {t('paymentMethodsWidget.taxes')}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
                {formatPrice(currencyData.tax_amount, currencyData.currency)}
            </Typography>            
          </Grid>
          <Grid item xs={4}>
            <Box display="flex" alignItems="center" gap={0.5}>
               
              <Typography variant="body2" color="text.secondary">
                {t('paymentMethodsWidget.deposit')}
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {formatPrice(currencyData.deposit_amount, currencyData.currency)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  // Rendu du contenu principal
  const renderContent = () => {
    if (loading && !data) {
      return (
        <Box>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('paymentMethodsWidget.loading')}
          </Typography>
        </Box>
      );
    }
    
    if (error) {
      return (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button size="small" onClick={handleRefresh}>
              {t('paymentMethodsWidget.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      );
    }
    
    if (!data || (data.cash_payments.length === 0 && data.online_payments.length === 0)) {
      return (
        <Alert severity="info">
          <Typography variant="body2">
            {t('paymentMethodsWidget.noData')}
          </Typography>
        </Alert>
      );
    }

    return (
      <Box>
        <Grid container spacing={3}>
          {/* Colonne Paiements Cash */}
          <Grid item xs={12} md={6}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <CashIcon sx={{ color: '#4caf50' }} />
                <Typography variant="h6" fontWeight="bold">
                  {t('paymentMethodsWidget.cashTitle')}
                </Typography>
                <Chip 
                  label={t('paymentMethodsWidget.currencyCount', { count: data.cash_payments.length })}
                  size="small"
                  sx={{ bgcolor: '#4caf50', color: 'white' }}
                />
              </Box>
              
              {data.cash_payments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('paymentMethodsWidget.noCash')}
                </Typography>
              ) : (
                data.cash_payments.map(currencyData => 
                  renderCurrencyCard(currencyData, 'cash')
                )
              )}
            </Box>
          </Grid>
          
          {/* Colonne Paiements En Ligne */}
          <Grid item xs={12} md={6}>
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <OnlineIcon sx={{ color: '#2196f3' }} />
                <Typography variant="h6" fontWeight="bold">
                  {t('paymentMethodsWidget.onlineTitle')}
                </Typography>
                <Chip 
                  label={t('paymentMethodsWidget.currencyCount', { count: data.online_payments.length })}
                  size="small"
                  sx={{ bgcolor: '#2196f3', color: 'white' }}
                />
              </Box>
              
              {data.online_payments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('paymentMethodsWidget.noOnline')}
                </Typography>
              ) : (
                data.online_payments.map(currencyData => 
                  renderCurrencyCard(currencyData, 'online')
                )
              )}
            </Box>
          </Grid>
        </Grid>
        
        {/* Bouton Charger Plus */}
        {hasMore && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={16} /> : <MoreIcon />}
              sx={{
                borderColor: '#9c27b0',
                color: '#9c27b0',
                '&:hover': {
                  borderColor: '#7b1fa2',
                  backgroundColor: 'rgba(156, 39, 176, 0.04)'
                }
              }}
            >
              {loadingMore ? t('paymentMethodsWidget.loadingMore') : t('paymentMethodsWidget.loadMore')}
            </Button>
          </Box>
        )}
        
        {/* Informations de pagination */}
        {data.pagination && (
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            mt={2}
            pt={2}
            borderTop="1px solid"
            borderColor="grey.200"
          >
            <Typography variant="caption" color="text.secondary">
              {t('paymentMethodsWidget.paginationInfo', { shown: data.cash_payments.length + data.online_payments.length, total: data.pagination.total })} • 
              {t('paymentMethodsWidget.lastUpdate')} {formatDateTime(new Date())}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 3 }}>
      {/* En-tête avec gradient et contrôles */}
      <Paper 
        elevation={2}
        sx={{
          background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
          color: 'white',
          '&:hover': {
            background: 'linear-gradient(45deg, #03dac4 30%, #6200ee 90%)'
          },
          p: 2,
          borderRadius: '12px 12px 0 0',
          cursor: 'pointer'
        }}
        onClick={handleToggle}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
              <PaymentIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {t('paymentMethodsWidget.title')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {data ? 
                  t('paymentMethodsWidget.subtitleWithData', { count: data.cash_payments.length + data.online_payments.length }) : 
                  t('paymentMethodsWidget.subtitleEmpty')
                }
                {(dateFrom || dateTo) && (
                  <Box component="span" sx={{ ml: 1, opacity: 0.8 }}>
                    • 📅 Filtré
                  </Box>
                )}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={t('paymentMethodsWidget.tooltipFilterDate')}>
              <IconButton 
                sx={{ 
                  color: 'white',
                  bgcolor: showFilters ? 'rgba(255,255,255,0.2)' : 'transparent'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFilters();
                }}
              >
                <FilterIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('paymentMethodsWidget.tooltipRefresh')}>
              <IconButton 
                sx={{ color: 'white' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRefresh();
                }}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton sx={{ color: 'white' }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Section des filtres de date */}
      <Collapse in={showFilters && expanded}>
        <Paper 
          elevation={1}
          sx={{
            borderRadius: 0,
            border: '1px solid #e0e0e0',
            borderTop: 'none',
            borderBottom: 'none'
          }}
        >
          <CardContent sx={{ p: 2, bgcolor: '#f8f9fa' }}>
            <Stack spacing={2}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <DateRangeIcon color="primary" />
                <Typography variant="subtitle2" fontWeight="bold">
                  {t('paymentMethodsWidget.filterByPeriod')}
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  type="date"
                  label={t('paymentMethodsWidget.dateFrom')}
                  value={dateFrom || ''}
                  onChange={handleDateFromChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ minWidth: 160 }}
                />
                
                <TextField
                  type="date"
                  label={t('paymentMethodsWidget.dateTo')}
                  value={dateTo || ''}
                  onChange={handleDateToChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  sx={{ minWidth: 160 }}
                />
                
                <Tooltip title={t('paymentMethodsWidget.tooltipClearFilters')}>
                  <IconButton 
                    onClick={handleClearFilters}
                    disabled={!dateFrom && !dateTo}
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </Tooltip>
                
                {(dateFrom || dateTo) && (
                  <Chip
                    label={`${dateFrom ? t('paymentMethodsWidget.dateChipFrom', { date: formatDate(dateFrom) }) : ''} ${dateTo ? t('paymentMethodsWidget.dateChipTo', { date: formatDate(dateTo) }) : ''}`}
                    color="primary"
                    variant="outlined"
                    size="small"
                    onDelete={handleClearFilters}
                  />
                )}
              </Stack>
              
              {(dateFrom || dateTo) && (
                <Typography variant="caption" color="text.secondary">
                  {t('paymentMethodsWidget.filterHint')}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Paper>
      </Collapse>

      {/* Contenu rétractable */}
      <Collapse in={expanded}>
        <Paper 
          elevation={2}
          sx={{
            borderRadius: '0 0 12px 12px',
            border: '1px solid #e0e0e0',
            borderTop: 'none'
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {renderContent()}
          </CardContent>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default OwnerPaymentMethodsWidget;